import { Box, Grid, Theme, Tooltip, Typography, useTheme } from '@mui/material';
import { ResponseExtended } from '../../../lib/readiness_gauge/readiness_gauge_instance';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ReactComponent as CheckIcon } from '../../../assets/icons/Correct.svg';
import { ReactComponent as CrossIcon } from '../../../assets/icons/Incorrect.svg';
import SDGIcon from '../../shared/decoration/SDGIcon';
import PrimaryDivider from '../../shared/decoration/PrimaryDivider';
import { BodyText } from '../../shared/typography/PrimaryTypography';
import { getGRIResourceLink, getSDGResourceLink } from '../../../lib/readiness_gauge/question';
import { Resource } from '@esg/esg-global-types';

interface KPIResponseProps {
  response: ResponseExtended;
  gri_resources: Array<Resource>;
  sdg_resources: Array<Resource>;
}

/**
 * Displays a Response with Question details for Readiness Gauge Results.
 * @param {ResponseExtended} response Response with Question details.
 * @param {Array<Resource>} gri_resources List of GRI related Resource Centre resources to search through for relevant compliance reference link.
 * @param {Array<Resource>} sdg_resources List of SDG related Resource Centre resources to search through for relevant sdg reference links.
 * @returns {JSX.Element}
 */
const KPIResponse = ({ response, gri_resources, sdg_resources }: KPIResponseProps) => {
  const theme: Theme = useTheme();

  return (
    <Grid container spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
      <Grid size={0.5}>
        <Tooltip
          title={<Typography fontSize="0.9rem">{response.question.description}</Typography>}
          arrow
        >
          <InfoOutlinedIcon color="primary" />
        </Tooltip>
      </Grid>
      <Grid size={9.5} sx={{ textAlign: 'left' }}>
        <Typography sx={{ my: 0.5 }}>{response.question.name}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, ml: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {response.question.sdg_references.map(
              (sdg_reference: string) =>
                !isNaN(Number(sdg_reference)) && (
                  <a
                    key={`${response.question.id}_${sdg_reference}`}
                    href={getSDGResourceLink(sdg_reference, sdg_resources)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <SDGIcon
                      key={`${response.question.id}_${sdg_reference}`}
                      sdg={Number(sdg_reference)}
                      width={40}
                    />
                  </a>
                )
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {response.question.compliance_references.map((compliance_ref: string) => (
              <a
                key={`${response.question.id}_${compliance_ref}`}
                href={getGRIResourceLink(compliance_ref, gri_resources)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: theme.palette.primary.main,
                  textDecoration: 'underline',
                  fontSize: '0.9rem'
                }}
              >
                <BodyText weight="Medium" sx={{ display: 'inline' }}>
                  {compliance_ref.replaceAll('GRI ', '')}
                </BodyText>
              </a>
            ))}
          </Box>
        </Box>
      </Grid>
      <Grid size={2}>
        {response.response ? (
          <CheckIcon height={30} stroke={theme.palette.success.main} />
        ) : (
          <CrossIcon height={30} fill="none" stroke={theme.palette.error.main} strokeWidth={4} />
        )}
      </Grid>
      <Grid size={12} sx={{ display: 'flex', justifyContent: 'center' }}>
        <PrimaryDivider opacity={0.2} sx={{ width: '90%' }} />
      </Grid>
    </Grid>
  );
};

export default KPIResponse;
